import $ from 'jquery';

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_FILES = 10;
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

export default class ApplicantFormHandler {
  constructor(config) {
    this._container = config.container;
    this._files = [];
    this._init();
  }

  _init() {
    const form          = this._container.find('form');
    const wrapperForm   = this._container.find('.wrapper-form');
    const wrapperMsg    = this._container.find('.wrapper-message');
    const submitBtn     = form.find('input[type="submit"]');
    const checkIcon     = form.find('.check-icon');
    const loadingIcon   = form.find('.loading-icon');
    const documentInput    = form.find('.applicant-documents__input');
    const documentList     = form.find('.applicant-documents__list');
    const documentsWrapper = documentInput.closest('.applicant-documents');
    const documentZone     = documentsWrapper.find('.applicant-documents__zone');

    const maxFiles = parseInt(form.attr('data-max-files'), 10) || DEFAULT_MAX_FILES;

    const showLoading = () => {
      submitBtn.prop('disabled', true);
      loadingIcon.addClass('active');
    };

    const hideLoading = () => {
      loadingIcon.removeClass('active');
      submitBtn.prop('disabled', false);
    };

    const clearErrors = () => {
      form.find('.error').removeClass('error');
      form.find('.input__error').remove();
    };

    const resetAltcha = () => {
      const widget = form.find('altcha-widget').get(0);
      if (widget && typeof widget.reset === 'function') {
        widget.reset();
      }
    };

    const scrollToFirstError = () => {
      const firstError = form.find('.error, .input__error').first();
      if (firstError.length) {
        $('html, body').animate({ scrollTop: firstError.offset().top - 80 }, 300);
      }
    };

    // ───── Multi-file collection (drag-pick-append + remove buttons) ─────
    const formatBytes = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const renderFileList = () => {
      documentList.empty();
      documentsWrapper.toggleClass('applicant-documents--has-files', this._files.length > 0);
      this._files.forEach((file, idx) => {
        const $li = $('<li/>', { class: 'applicant-documents__item' });
        $li.append($('<span/>', { class: 'applicant-documents__item-name', text: file.name + ' (' + formatBytes(file.size) + ')' }));
        const $btn = $('<button/>', {
          type: 'button',
          class: 'applicant-documents__item-remove',
          'aria-label': 'Datei entfernen',
          text: '×',
        });
        $btn.on('click', () => {
          this._files.splice(idx, 1);
          renderFileList();
        });
        $li.append($btn);
        documentList.append($li);
      });
    };

    const fileIsValid = (file) => {
      if (file.size > DEFAULT_MAX_BYTES) {
        documentsWrapper.addClass('error');
        documentZone.before('<div class="input__error">Datei zu groß (max. 10 MB): ' + file.name + '</div>');
        return false;
      }
      if (file.type && !ALLOWED_MIME.includes(file.type)) {
        documentsWrapper.addClass('error');
        documentZone.before('<div class="input__error">Ungültiges Format (nur PDF, JPG, PNG): ' + file.name + '</div>');
        return false;
      }
      return true;
    };

    const clearDocumentFieldErrors = () => {
      documentsWrapper.find('.input__error').remove();
      documentsWrapper.removeClass('error');
    };

    const appendFilesFromList = (files) => {
      files.forEach((file) => {
        if (this._files.length >= maxFiles) {
          documentsWrapper.addClass('error');
          documentZone.before('<div class="input__error">Maximal ' + maxFiles + ' Dateien erlaubt.</div>');
          return;
        }
        if (fileIsValid(file)) {
          this._files.push(file);
        }
      });
    };

    documentInput.on('change', (event) => {
      clearDocumentFieldErrors();

      const picked = Array.from(event.target.files || []);
      appendFilesFromList(picked);
      // Reset the native input so the same file can be re-selected if removed.
      event.target.value = '';
      renderFileList();
    });

    // Drag-and-drop onto the documents zone
    let dragDepth = 0;
    const resetDragState = () => {
      dragDepth = 0;
      documentZone.removeClass('applicant-documents__zone--drag');
    };

    documentZone.on('dragenter', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth += 1;
      documentZone.addClass('applicant-documents__zone--drag');
    });

    documentZone.on('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth -= 1;
      if (dragDepth <= 0) {
        resetDragState();
      }
    });

    documentZone.on('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });

    documentZone.on('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      resetDragState();
      clearDocumentFieldErrors();
      const dt = e.originalEvent && e.originalEvent.dataTransfer;
      if (!dt || !dt.files || !dt.files.length) {
        return;
      }
      appendFilesFromList(Array.from(dt.files));
      renderFileList();
    });

    $(document).on('dragend', resetDragState);

    // ───── Response handling ─────
    const handleResponse = (response) => {
      if (response && response.status === 'success') {
        submitBtn.addClass('btn_success');
        submitBtn.prop('disabled', true);
        loadingIcon.removeClass('active');
        checkIcon.addClass('active');

        setTimeout(() => {
          checkIcon.removeClass('active');
          wrapperForm.hide();
          // Subline only makes sense with the form; keep headline + success message.
          this._container.find('.applicant-form-section__subline').hide();
          this._container.find('.applicant-form-section__content .title').removeClass('title_mb-16');
          wrapperMsg.find('.message-success').html(response.message || '');
          wrapperMsg.show();

          if (typeof window.dataLayer === 'undefined') { window.dataLayer = []; }
          window.dataLayer.push({
            event: 'applicant_form_submitted',
            job_id: form.find('#applicant-job-id').val() || '',
            job_title: form.find('#applicant-job-title').val() || '',
          });
        }, 1000);

      } else if (response && response.status === 'validation') {
        hideLoading();
        resetAltcha();

        if (response.errors) {
          $.each(response.errors, (field, message) => {
            const fieldKey = field.replace(/^newApplicant\./, '');
            // File-related errors live under "files" or "files.<index>".
            if (fieldKey === 'files' || fieldKey.indexOf('files.') === 0) {
              documentsWrapper.addClass('error');
              documentZone.before('<div class="input__error">' + message + '</div>');
              return;
            }
            const input = form.find('#inp-' + fieldKey);
            if (input.length) {
              input.parent().addClass('error');
              input.before('<div class="input__error">' + message + '</div>');
            } else {
              form.prepend('<div class="input__error input__error--global">' + message + '</div>');
            }
          });
          scrollToFirstError();
        }

      } else if (response && response.status === 'error') {
        hideLoading();
        const msg = response.message || 'Bitte versuchen Sie es später noch einmal.';
        wrapperMsg.find('.message-success').html(msg);
        wrapperMsg.show();

      } else {
        hideLoading();
      }
    };

    this._initWhatsAppModal();

    form.on('submit', (event) => {
      event.preventDefault();
      const formEl = form.get(0);
      if (formEl && typeof formEl.checkValidity === 'function' && !formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      clearErrors();

      if (this._files.length > maxFiles) {
        documentsWrapper.addClass('error');
        documentZone.before('<div class="input__error">Maximal ' + maxFiles + ' Dateien erlaubt.</div>');
        scrollToFirstError();
        return;
      }

      showLoading();

      // Inject UTM cookies
      const utmFields = {
        '#applicant-utm-source':   'utm_source',
        '#applicant-utm-medium':   'utm_medium',
        '#applicant-utm-campaign': 'utm_campaign',
        '#applicant-utm-term':     'utm_term',
        '#applicant-utm-content':  'utm_content',
      };
      if (typeof window.getCookie === 'function') {
        $.each(utmFields, (selector, cookieName) => {
          const val = window.getCookie(cookieName);
          if (val && val !== 'null') {
            form.find(selector).val(val);
          }
        });
      }

      const canonical = $("link[rel='canonical']").attr('href');
      if (canonical) {
        form.find('#applicant-landingpage').val(canonical);
      }

      // Build FormData manually so we ship our curated file list (not the input's).
      // Files MUST be sent under the Extbase plugin namespace, otherwise the
      // RequestBuilder strips them before the controller sees them.
      const formData = new FormData();
      const serialized = form.serializeArray();
      serialized.forEach((entry) => {
        formData.append(entry.name, entry.value);
      });
      this._files.forEach((file) => {
        formData.append('tx_bohlmeier_applicantrequest[files][]', file, file.name);
      });

      $.ajax({
        url:         form.attr('action'),
        type:        'POST',
        data:        formData,
        dataType:    'json',
        processData: false,
        contentType: false,
        success: (response) => handleResponse(response),
        error:   ()         => hideLoading(),
      });
    });
  }

  /**
   * Wires the "Apply via WhatsApp" trigger + modal.
   *
   * On open, rebuilds the wa.me href so the prefilled text matches the
   * spec format:
   *   "Hallo Bohlmeier Team, ich interessiere mich für die Stelle als
   *    <Job Title> (Job-ID: <Page UID>) und möchte mich gerne bewerben. <Source-ID>"
   *
   * <Source-ID> is appended only when a utm_source cookie is present
   * (set by campaign.js on landing). Falls back gracefully to the
   * server-rendered href if cookies / window.getCookie are unavailable.
   */
  _initWhatsAppModal() {
    const trigger  = this._container.find('.applicant-whatsapp-trigger');
    const modal    = this._container.find('.applicant-whatsapp-modal');
    const openLink = modal.find('.applicant-whatsapp-modal__open');

    if (!trigger.length || !modal.length) {
      return;
    }

    const buildHref = () => {
      const baseUrl  = openLink.attr('data-base-url') || '';
      const template = (openLink.attr('data-prefill') || '').replace(/\s+/g, ' ').trim();
      if (!baseUrl || !template) {
        return;
      }

      let sourceSuffix = '';
      if (typeof window.getCookie === 'function') {
        const utmSource = window.getCookie('utm_source');
        if (utmSource && utmSource !== 'null' && utmSource !== '') {
          sourceSuffix = ' Ref: ' + utmSource;
        }
      }

      const finalText = encodeURIComponent(template + sourceSuffix);
      openLink.attr('href', baseUrl + '?text=' + finalText);
    };

    const openModal = () => {
      buildHref();
      modal.addClass('is-open').attr('aria-hidden', 'false');
      $('body').addClass('is-offscroll');
      setTimeout(() => {
        const focusTarget = modal.find('.modal__close').get(0);
        if (focusTarget) {
          focusTarget.focus();
        }
      }, 50);
    };

    const closeModal = () => {
      modal.removeClass('is-open').attr('aria-hidden', 'true');
      $('body').removeClass('is-offscroll');
      trigger.focus();
    };

    trigger.on('click', (e) => {
      e.preventDefault();
      openModal();
    });

    modal.on('click', (e) => {
      // Close on backdrop click or click on the close control (same pattern as contact-request modal)
      if (
        e.target === modal.get(0) ||
        $(e.target).closest('.modal__close').length
      ) {
        e.preventDefault();
        closeModal();
      }
    });

    modal.find('.modal__close').off('keydown.applicantWhatsapp').on('keydown.applicantWhatsapp', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        closeModal();
      }
    });

    $(document).on('keydown.applicantWhatsapp', (e) => {
      if (e.key === 'Escape' && modal.hasClass('is-open')) {
        closeModal();
      }
    });
  }
}
